import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore";
import { getMe } from "../apis/auth";
import { useServiceRequests } from "../hooks";
import {
  Container,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Select,
  type SelectOption,
} from "../components/ui";
import { CreateServiceRequestModal } from "../components/CreateServiceRequestModal";

const STATUS_OPTIONS: SelectOption[] = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "assigned", label: "Assigned" },
  { value: "inProgress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "rejected", label: "Rejected" },
];

function getStatusBadgeClass(status: string) {
  switch (status) {
    case "pending":
      return "bg-amber-100 text-amber-800";
    case "assigned":
      return "bg-blue-100 text-blue-800";
    case "inProgress":
      return "bg-indigo-100 text-indigo-800";
    case "completed":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    default:
      return "bg-secondary-100 text-secondary-700";
  }
}

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { user, token, setAuth, logout, isAuthenticated } = useAuthStore();
  const [statusFilter, setStatusFilter] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { data: requestsData, isLoading } = useServiceRequests({
    status: statusFilter || undefined,
    page: 1,
    limit: 50,
  });

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    if (!user && token) {
      getMe()
        .then((u) => setAuth(u, token))
        .catch(() => {
          logout();
          navigate("/login", { replace: true });
        });
    }
  }, [token, user, navigate, setAuth, logout]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  if (!isAuthenticated && !token) return null;

  const requests = requestsData?.requests ?? [];

  return (
    <div className="min-h-screen bg-secondary-50 py-8 px-4">
      <Container>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">
              Service Requests
            </h1>
            <p className="text-secondary-600 mt-1">
              {user?.profile?.fullName || user?.phone || "Partner"} dashboard
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/")}>
              Registration
            </Button>
            <Button variant="primary" onClick={() => setCreateModalOpen(true)}>
              New Request
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Your Requests</CardTitle>
            <div className="w-40">
              <Select
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                placeholder="Filter by status"
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-secondary-600 py-8 text-center">
                Loading requests...
              </p>
            ) : requests.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-secondary-600 mb-4">
                  No service requests yet
                </p>
                <Button onClick={() => setCreateModalOpen(true)}>
                  Create your first request
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map((req: any) => (
                  <div
                    key={req._id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 rounded-lg border border-secondary-200 bg-white"
                  >
                    <div>
                      <p className="font-medium text-secondary-900">
                        {req.serviceId?.name ?? "Service"}
                      </p>
                      <p className="text-sm text-secondary-600">
                        {req.requestId} · {req.cost?.estimated ? `₹${req.cost.estimated}` : ""}
                      </p>
                    </div>
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                        req.status
                      )}`}
                    >
                      {req.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </Container>

      <CreateServiceRequestModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
    </div>
  );
}
